import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ActivationCodeService } from '../services/activationCodeService';
import { getStatusDisplay } from '../utils/activationCodeUtils';
import TargetTitle from '../components/TargetTitle';
import { getUsageRate } from '../utils/batchStatus';
import toast from 'react-hot-toast';

const BatchDetailPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  
  const [batch, setBatch] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });

  // Load batch details
  const loadBatch = async () => {
    try {
      setLoading(true);
      const response = await ActivationCodeService.getBatch(batchId);
      
      if (response.success) {
        setBatch(response.data);
      } else {
        throw new Error(response.message || 'Failed to load batch details');
      }
    } catch (error) {
      console.error('Error loading batch:', error);
      toast.error('Có lỗi xảy ra khi tải thông tin lô mã');
    } finally {
      setLoading(false);
    }
  };

  // Load codes for this batch
  const loadCodes = async () => {
    try {
      const response = await ActivationCodeService.getCodesByBatch(batchId, {
        page: pagination.current_page,
        per_page: pagination.per_page
      });
      
      if (response.success) {
        setCodes(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          last_page: response.pagination?.last_page || 1,
          from: response.pagination?.from || 0,
          to: response.pagination?.to || 0
        }));
      } else {
        throw new Error(response.message || 'Failed to load codes');
      }
    } catch (error) {
      console.error('Error loading codes:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách mã kích hoạt');
    }
  };

  useEffect(() => {
    if (batchId) {
      loadBatch();
      loadCodes();
    }
  }, [batchId, pagination.current_page]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num || 0);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      
      const exportParams = {
        format: format,
        batch_id: batchId
      };

      const response = await ActivationCodeService.exportCodes(exportParams);
      
      if (response.status === 200) {
        // Create download link for the file
        const blob = new Blob([response.data], { 
          type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `batch_${batchId}_codes_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Đã xuất ${codes.length} mã ra file ${format.toUpperCase()}`);
      } else {
        throw new Error('Export failed');
      }
      
    } catch (error) {
      console.error('Error exporting codes:', error);
      
      if (error.response?.data?.message) {
        toast.error(`Lỗi xuất file: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Lỗi xuất file: ${error.message}`);
      } else {
        toast.error('Có lỗi xảy ra khi xuất mã');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) return null;

    const pages = [];
    const currentPage = pagination.current_page || 1;
    const lastPage = pagination.last_page || 1;

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className="px-3 py-2 text-sm border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Trước
      </button>
    );

    // Page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(lastPage, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm border-t border-b border-gray-300 hover:bg-gray-50 ${
            i === currentPage 
              ? 'bg-blue-600 text-white border-blue-600' 
              : 'bg-white text-gray-700'
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= lastPage}
        className="px-3 py-2 text-sm border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Sau
      </button>
    );

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="text-sm text-gray-700">
          Hiển thị {pagination.from || 0}-{pagination.to || codes.length} của {pagination.total} kết quả
        </div>
        <div className="flex space-x-1">
          {pages}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">📦</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Không tìm thấy lô mã</h2>
          <p className="text-gray-600 mb-4">Lô mã này không tồn tại hoặc đã bị xóa</p>
          <Link
            to="/books/activation-codes"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Quay lại danh sách
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-10xl mx-auto ">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/admin/books" className="text-blue-600 hover:text-blue-700">
                Quản lý sách
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to="/books/activation-codes" className="text-blue-600 hover:text-blue-700">
                Mã kích hoạt
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900 font-medium">Chi tiết lô mã</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{batch.name}</h1>
              <p className="text-gray-600 mt-1">
                Chi tiết lô mã kích hoạt - {batch.sku}
              </p>
            </div>
          <Link
            to="/books/activation-codes"
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            ← Quay lại
          </Link>
          </div>
        </div>

        {/* Batch Info */}
        <div className="bg-white rounded-lg shadow-sm border mb-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Thông tin lô mã</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatNumber(batch.total_codes)}</div>
                <div className="text-sm text-gray-600">Tổng số mã</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{formatNumber(batch.used_codes)}</div>
                <div className="text-sm text-gray-600">Đã sử dụng</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{formatNumber(batch.unused_codes)}</div>
                <div className="text-sm text-gray-600">Chưa sử dụng</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{getUsageRate(batch)}%</div>
                <div className="text-sm text-gray-600">Tỷ lệ sử dụng</div>
              </div>
            </div>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Thông tin cơ bản</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-gray-500">Tên lô:</dt>
                    <dd className="text-sm text-gray-900">{batch.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">SKU:</dt>
                    <dd className="text-sm text-gray-900">{batch.sku}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Sách:</dt>
                    <dd className="text-sm text-gray-900"><TargetTitle item={batch} /></dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Code Prefix:</dt>
                    <dd className="text-sm text-gray-900">{batch.code_prefix}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Code Middle:</dt>
                    <dd className="text-sm text-gray-900">{batch.code_middle}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Số thiết bị tối đa:</dt>
                    <dd className="text-sm text-gray-900">
                      {batch.max_download_limit != null
                        ? `${batch.max_download_limit} thiết bị`
                        : '—'}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Thông tin thời gian</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-gray-500">Ngày tạo:</dt>
                    <dd className="text-sm text-gray-900">{formatDate(batch.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Hết hạn:</dt>
                    <dd className="text-sm text-gray-900">{formatDate(batch.expires_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Mã đã hết hạn:</dt>
                    <dd className="text-sm text-gray-900">{formatNumber(batch.expired_codes)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Mã đã thu hồi:</dt>
                    <dd className="text-sm text-gray-900">{formatNumber(batch.revoked_codes)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Codes List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Danh sách mã kích hoạt</h2>
              <div className="flex items-center space-x-2">
                {/* Export buttons */}
                <div className="relative group">
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                    📥 Xuất mã
                  </button>
                  <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                    <button
                      onClick={() => handleExport('csv')}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      📊 CSV
                    </button>
                <button
                    onClick={() => handleExport('xlsx')}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                    📈 Excel
                </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      📄 PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mã kích hoạt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngày tạo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hết hạn
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sử dụng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      <div className="text-4xl mb-2">📭</div>
                      Không có mã kích hoạt nào
                    </td>
                  </tr>
                ) : (
                  codes.map((code) => {
                    const statusDisplay = getStatusDisplay(code.status);
                    return (
                      <tr key={code._id || code.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                            {code.code}
                          </code>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.badgeClass}`}>
                            {statusDisplay.icon} {statusDisplay.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(code.created_at)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {formatDate(code.expires_at)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {code.used_at ? formatDate(code.used_at) : 'N/A'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default BatchDetailPage;
